/**
 * Subgraph re-collapse — put the boxes back after a glTF round-trip.
 *
 * `inlineSubgraphs` flattens `TRACE_subgraph` invocations so exported KHR is
 * standard-ops-only (#601). That is correct for execution and lossy for
 * STRUCTURE: reimport a flattened graph and the author sees a flat pile where
 * they had subgraphs. This is the other half — it reads the tags flattening
 * left behind and reconstructs the definitions.
 *
 * Same identity model as macros, applied to the other authoring construct
 * (DESIGN_PRINCIPLES §6): metadata is the identity, it survives glTF through
 * `extras`, and anything that fails to reconstruct **stays flat by design**.
 * A flat graph still executes correctly — it has just lost its shape — so the
 * failure mode of every check below is "leave it alone", never "throw" and
 * never "guess".
 *
 * What makes reconstruction possible rather than a heuristic: each member
 * carries its index within the original body, and the first member carries a
 * manifest with the body's interface (in BODY index space) and its variables.
 * So the boundary crossings can be matched back to the sockets the author
 * named instead of being regenerated as `in0`/`out0`.
 *
 * ── What is NOT reconstructed ──
 *
 * Nested subgraphs recover ONE level, and it is the OUTER box that comes back.
 * Flattening is depth-first, so the outer splice copies nodes the inner splice
 * already tagged and overwrites those tags — the outer instance is the one that
 * survives. That is the better half to keep: the author gets their top-level
 * box back, with one level of nesting dissolved inside it, rather than an inner
 * box floating in a flat parent.
 */

import { Logger } from '../../utils/Logger';
import {
    SUBGRAPH_INDEX_KEY,
    SUBGRAPH_MANIFEST_KEY,
    TRACE_SUBGRAPH_KEY,
    type SubgraphManifest,
} from './subgraphInline';
import type {
    Declaration,
    GraphData,
    GraphNode,
    GraphEvent,
    GraphVariable,
    TypeDefinition,
} from '../../scenedata/behaviors/behaviorgraphdata';

/** One flattened invocation found in the graph. */
interface TaggedGroup {
    instanceId: string;
    manifest: SubgraphManifest;
    /** Parent node index by body index. */
    members: number[];
}

/** Does any node carry a re-collapse tag? */
export function hasSubgraphTags(graph: GraphData): boolean {
    return graph.nodes.some(n => typeof n.metadata?.[TRACE_SUBGRAPH_KEY] === 'string');
}

/**
 * Collect complete tagged groups. A group is usable only when every body index
 * from 0..n-1 is present exactly once and the manifest is on index 0 — anything
 * else means a lossy rewrite or a hand edit, and the group is skipped.
 */
function collectGroups(graph: GraphData): TaggedGroup[] {
    const byId = new Map<string, { members: Map<number, number>; manifest?: SubgraphManifest; broken: boolean }>();

    for (let i = 0; i < graph.nodes.length; i++) {
        const meta = graph.nodes[i].metadata;
        const id = meta?.[TRACE_SUBGRAPH_KEY];
        if (!meta || typeof id !== 'string') continue;
        const bodyIndex = meta[SUBGRAPH_INDEX_KEY];
        let group = byId.get(id);
        if (!group) {
            group = { members: new Map(), broken: false };
            byId.set(id, group);
        }
        if (typeof bodyIndex !== 'number' || group.members.has(bodyIndex)) {
            group.broken = true;
            continue;
        }
        group.members.set(bodyIndex, i);
        const manifest = meta[SUBGRAPH_MANIFEST_KEY];
        if (bodyIndex === 0 && manifest && typeof manifest === 'object') {
            group.manifest = manifest as SubgraphManifest;
        }
    }

    const groups: TaggedGroup[] = [];
    for (const [instanceId, group] of byId) {
        if (group.broken || !group.manifest) continue;
        // Trust the manifest's count, not the number of members found: a group
        // truncated at the end still looks contiguous from 0, and rebuilding
        // from it would silently drop the body's last nodes.
        const size = group.manifest.nodeCount;
        if (typeof size !== 'number' || group.members.size !== size) continue;
        const members: number[] = [];
        let contiguous = true;
        for (let b = 0; b < size; b++) {
            const parentIndex = group.members.get(b);
            if (parentIndex === undefined) {
                contiguous = false;
                break;
            }
            members.push(parentIndex);
        }
        if (!contiguous) continue;
        groups.push({ instanceId, manifest: group.manifest, members });
    }
    return groups;
}

/** Strip the re-collapse tags from a node's metadata, dropping it when empty. */
function withoutTags(node: GraphNode): GraphNode {
    if (!node.metadata) return node;
    const metadata = { ...node.metadata };
    delete metadata[TRACE_SUBGRAPH_KEY];
    delete metadata[SUBGRAPH_INDEX_KEY];
    delete metadata[SUBGRAPH_MANIFEST_KEY];
    const copy = { ...node };
    if (Object.keys(metadata).length > 0) copy.metadata = metadata;
    else delete copy.metadata;
    return copy;
}

/**
 * Rebuild one definition graph from a group's member nodes.
 *
 * Returns null when a member references something outside the group that the
 * recorded interface does not account for — the graph then stays flat, because
 * collapsing would silently drop that reference.
 */
function buildDefinition(graph: GraphData, group: TaggedGroup): GraphData | null {
    const { members, manifest } = group;
    const bodyIndexOf = new Map<number, number>();
    members.forEach((parentIndex, bodyIndex) => bodyIndexOf.set(parentIndex, bodyIndex));

    const types: TypeDefinition[] = [];
    const declarations: Declaration[] = [];
    const typeIndex = (t: TypeDefinition): number => {
        const found = types.findIndex(x => x.signature === t.signature);
        return found === -1 ? types.push({ ...t }) - 1 : found;
    };
    const declIndex = (d: Declaration): number => {
        if (!d.extensions) {
            const found = declarations.findIndex(x => x.op === d.op && !x.extensions);
            if (found !== -1) return found;
        }
        return declarations.push(structuredClone(d)) - 1;
    };

    // Variables come back with their authored names, in body order.
    const variables: GraphVariable[] = [];
    const parentVarToBody = new Map<number, number>();
    for (const entry of manifest.variables) {
        const parentVar = graph.variables[entry.parentIndex];
        if (!parentVar) return null; // the copy is gone — cannot restore the scope
        parentVarToBody.set(entry.parentIndex, variables.length);
        // The type index is the PARENT's; re-home it in the definition's table.
        variables.push({
            ...structuredClone(parentVar),
            name: entry.name,
            type: typeIndex(graph.types[parentVar.type] ?? { signature: 'float' }),
        });
    }

    const isVariableOp = (op: string): boolean => op === 'variable/get' || op === 'variable/set';

    // Custom events come back the same way (manifests predating inlined
    // events carry none — then any member send/receive leaves the group flat).
    const events: GraphEvent[] = [];
    const parentEventToBody = new Map<number, number>();
    for (const parentIndex of manifest.events ?? []) {
        const parentEvent = graph.events?.[parentIndex];
        if (!parentEvent) return null;
        const copy = structuredClone(parentEvent);
        if (copy.values) {
            for (const value of Object.values(copy.values)) value.type = typeIndex(graph.types[value.type] ?? { signature: 'string' });
        }
        parentEventToBody.set(parentIndex, events.length);
        events.push(copy);
    }
    const isEventOp = (op: string): boolean => op === 'event/send' || op === 'event/receive';

    const nodes: GraphNode[] = [];
    for (const parentIndex of members) {
        const source = graph.nodes[parentIndex];
        const node = structuredClone(withoutTags(source));
        const op = graph.declarations[source.declaration]?.op ?? '';
        node.declaration = declIndex(graph.declarations[source.declaration] ?? { op });

        if (node.configuration) {
            for (const entry of Object.values(node.configuration)) {
                entry.type = typeIndex(graph.types[entry.type] ?? { signature: 'string' });
            }
            // Variable references are indices; map them into the definition's table.
            const variableConfig = node.configuration['variable'];
            if (variableConfig && isVariableOp(op)) {
                const bodyVar = parentVarToBody.get(Number(variableConfig.value[0]));
                if (bodyVar === undefined) return null; // points outside the group's scope
                variableConfig.value = [bodyVar];
            }
            const eventConfig = node.configuration['event'];
            if (eventConfig && isEventOp(op)) {
                const bodyEvent = parentEventToBody.get(Number(eventConfig.value[0]));
                if (bodyEvent === undefined) return null;
                eventConfig.value = [bodyEvent];
            }
        }

        if (node.flows) {
            for (const [socket, flow] of Object.entries(node.flows)) {
                const inner = bodyIndexOf.get(flow.node);
                if (inner !== undefined) {
                    flow.node = inner;
                    continue;
                }
                // Leaves the group: must be a declared flow output, which the
                // invocation node will carry instead.
                const named = Object.entries(manifest.iface.outputFlowSockets ?? {}).some(
                    ([, ref]) => ref.node === bodyIndexOf.get(parentIndex) && ref.socket === socket,
                );
                if (!named) return null;
                delete node.flows[socket];
            }
        }

        if (node.values) {
            for (const value of Object.values(node.values)) {
                if ('node' in value) {
                    const inner = bodyIndexOf.get(value.node);
                    if (inner === undefined) return null; // fed from outside: interface cannot express it here
                    value.node = inner;
                } else {
                    value.type = typeIndex(graph.types[value.type] ?? { signature: 'string' });
                }
            }
        }

        nodes.push(node);
    }

    return {
        types,
        declarations,
        variables,
        events,
        nodes,
        ...(manifest.iface.inputFlowSockets ? { inputFlowSockets: structuredClone(manifest.iface.inputFlowSockets) } : {}),
        ...(manifest.iface.outputFlowSockets ? { outputFlowSockets: structuredClone(manifest.iface.outputFlowSockets) } : {}),
        ...(manifest.iface.inputValueSockets ? { inputValueSockets: structuredClone(manifest.iface.inputValueSockets) } : {}),
        ...(manifest.iface.outputValueSockets
            ? { outputValueSockets: structuredClone(manifest.iface.outputValueSockets) }
            : {}),
    };
}

/**
 * Rebuild subgraph invocations from the tags `inlineSubgraphs` left behind.
 *
 * @param graph The flattened graph (never mutated).
 * @returns `{ graphs, activeGraph }` in multi-graph form, where `graphs[0]` is
 *   the rewritten main graph and the rest are recovered definitions — or null
 *   when nothing could be re-collapsed, in which case the caller keeps the flat
 *   graph as-is.
 */
export function recollapseSubgraphs(graph: GraphData): { graphs: GraphData[]; activeGraph: number } | null {
    if (!hasSubgraphTags(graph)) return null;

    const groups = collectGroups(graph);
    if (groups.length === 0) return null;

    const out = structuredClone(graph);
    const definitions: GraphData[] = [];
    /** Parent node index → the invocation node that replaces its group. */
    const replacedBy = new Map<number, { entry: number; group: TaggedGroup; graphIndex: number }>();
    const claimedVariables = new Set<number>();
    const claimedEvents = new Set<number>();

    for (const group of groups) {
        // Belt-and-braces: flattening overwrites inner tags with outer ones, so
        // two live groups should never share a member. If one somehow does
        // (hand-edited extras), take the first and leave the other flat rather
        // than splitting a node between two definitions.
        if (group.members.some(m => replacedBy.has(m))) continue;

        const definition = buildDefinition(out, group);
        if (!definition) {
            Logger.log(`[recollapseSubgraphs] instance ${group.instanceId}: not reconstructable — left flat`);
            continue;
        }

        // graphs[0] is the main graph, so definitions start at 1.
        const graphIndex = definitions.push(definition);
        const entry = group.members[0];
        for (const m of group.members) replacedBy.set(m, { entry, group, graphIndex });
        for (const v of group.manifest.variables) claimedVariables.add(v.parentIndex);
        // Only private (internal) copies move out; an external-id event is
        // shared with the parent and may still be addressed there.
        for (const e of group.manifest.events ?? []) if (!out.events?.[e]?.id) claimedEvents.add(e);
    }

    if (definitions.length === 0) return null;

    // ── Rewrite the parent ──────────────────────────────────────────────────

    // The invocation node replaces the group's first member in place, so
    // existing references to that index keep working until compaction.
    for (const [, { entry, group, graphIndex }] of replacedBy) {
        const node = out.nodes[entry];
        if (node.metadata?.[SUBGRAPH_MANIFEST_KEY] === undefined) continue; // already rewritten
        const declaration = out.declarations.push({
            op: group.manifest.op,
            extensions: { TRACE_subgraph: { graph: graphIndex } },
        }) - 1;
        const metadata = withoutTags(node).metadata;
        out.nodes[entry] = {
            declaration,
            ...(metadata ? { metadata } : {}),
        };
    }

    // Member outbound flows that left the group become the invocation's own,
    // keyed by the interface socket name the author gave them.
    for (const [, { entry, group }] of replacedBy) {
        for (const [name, ref] of Object.entries(group.manifest.iface.outputFlowSockets ?? {})) {
            const member = group.members[ref.node];
            if (member === undefined) continue;
            const flow = graph.nodes[member]?.flows?.[ref.socket];
            if (!flow || group.members.includes(flow.node)) continue;
            (out.nodes[entry].flows ??= {})[name] = { ...flow };
        }
    }

    // External references INTO members retarget to the invocation, addressed
    // by interface name rather than by internal socket.
    const memberEntry = (index: number): { entry: number; group: TaggedGroup } | undefined => replacedBy.get(index);
    for (let i = 0; i < out.nodes.length; i++) {
        if (replacedBy.has(i) && out.nodes[i].declaration !== undefined && memberEntry(i)?.entry !== i) continue;
        const node = out.nodes[i];
        if (node.flows) {
            for (const [socket, flow] of Object.entries(node.flows)) {
                const hit = memberEntry(flow.node);
                if (!hit || hit.group.members.includes(i)) continue;
                const bodyIndex = hit.group.members.indexOf(flow.node);
                const named = Object.entries(hit.group.manifest.iface.inputFlowSockets ?? {}).find(
                    ([, ref]) => ref.node === bodyIndex && ref.socket === flow.socket,
                );
                node.flows[socket] = { node: hit.entry, socket: named?.[0] ?? 'in' };
            }
        }
        if (node.values) {
            for (const [socket, value] of Object.entries(node.values)) {
                if (!('node' in value)) continue;
                const hit = memberEntry(value.node);
                if (!hit || hit.group.members.includes(i)) continue;
                const bodyIndex = hit.group.members.indexOf(value.node);
                const named = Object.entries(hit.group.manifest.iface.outputValueSockets ?? {}).find(
                    ([, ref]) => ref.node === bodyIndex && ref.socket === value.socket,
                );
                node.values[socket] = { node: hit.entry, socket: named?.[0] ?? value.socket };
            }
        }
    }

    // ── Drop the absorbed members and compact ───────────────────────────────

    const removed = new Set<number>();
    for (const [, { entry, group }] of replacedBy) {
        for (const m of group.members) if (m !== entry) removed.add(m);
    }

    const indexMap = new Map<number, number>();
    let next = 0;
    for (let i = 0; i < out.nodes.length; i++) {
        if (!removed.has(i)) indexMap.set(i, next++);
    }
    for (const [, { entry }] of replacedBy) {
        for (const m of removed) indexMap.set(m, indexMap.get(entry) ?? 0);
    }
    out.nodes = out.nodes.filter((_, i) => !removed.has(i));
    for (const node of out.nodes) {
        if (node.flows) for (const flow of Object.values(node.flows)) flow.node = indexMap.get(flow.node) ?? flow.node;
        if (node.values) {
            for (const value of Object.values(node.values)) {
                if ('node' in value) value.node = indexMap.get(value.node) ?? value.node;
            }
        }
    }

    // The private variable copies belong to the definitions now.
    if (claimedVariables.size > 0) {
        const varMap = new Map<number, number>();
        const kept: GraphVariable[] = [];
        for (let i = 0; i < out.variables.length; i++) {
            if (claimedVariables.has(i)) continue;
            varMap.set(i, kept.length);
            kept.push(out.variables[i]);
        }
        out.variables = kept;
        for (const node of out.nodes) {
            const op = out.declarations[node.declaration]?.op;
            const variableConfig = node.configuration?.['variable'];
            if (!variableConfig || (op !== 'variable/get' && op !== 'variable/set')) continue;
            const authored = Number(variableConfig.value[0]);
            variableConfig.value = [varMap.get(authored) ?? authored];
        }
    }

    if (claimedEvents.size > 0) {
        const eventMap = new Map<number, number>();
        const kept: GraphEvent[] = [];
        (out.events ?? []).forEach((event, i) => {
            if (claimedEvents.has(i)) return;
            eventMap.set(i, kept.length);
            kept.push(event);
        });
        out.events = kept;
        for (const node of out.nodes) {
            const op = out.declarations[node.declaration]?.op;
            const eventConfig = node.configuration?.['event'];
            if (!eventConfig || (op !== 'event/send' && op !== 'event/receive')) continue;
            const authored = Number(eventConfig.value[0]);
            eventConfig.value = [eventMap.get(authored) ?? authored];
        }
    }

    // Declarations left unreferenced by the absorbed members.
    const referenced = new Set(out.nodes.map(n => n.declaration));
    const declMap = new Map<number, number>();
    const keptDecls: Declaration[] = [];
    for (let i = 0; i < out.declarations.length; i++) {
        if (!referenced.has(i)) continue;
        declMap.set(i, keptDecls.length);
        keptDecls.push(out.declarations[i]);
    }
    out.declarations = keptDecls;
    for (const node of out.nodes) node.declaration = declMap.get(node.declaration) ?? node.declaration;

    Logger.log(`[recollapseSubgraphs] recovered ${definitions.length} subgraph invocation(s)`);
    return { graphs: [out, ...definitions], activeGraph: 0 };
}
